import React, { useState, useEffect } from 'react';
import { useApp } from '../../../contexts/AppContext';
import { useData } from '../../../hooks/useData';
import { TABLES } from '../../../constants';
import { useErrorLogging, ERROR_SEVERITY, ERROR_CATEGORY } from '../../../utils/errorLogging';
import styles from './BOQTemplateModal.module.css';
import modalStyles from '../../../styles/modals.module.css';

const BOQTemplateModal = ({ open, onClose, onSubmitRef, categoryId, onSubmit }) => {
  const { currentProject, userProfile } = useApp();
  const { logError } = useErrorLogging();
  const [selectedItems, setSelectedItems] = useState(new Map());
  const [editedText, setEditedText] = useState(new Map());
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Get template items for this category
  const { data: templateItems } = useData(TABLES.MASTER_BOQ_TEMPLATE_ITEMS, {
    select: '*',
    filter: {
      category_id: categoryId,
      created_by: userProfile?.id,
    },
    orderBy: { column: 'subject', ascending: true },
  });

  // Get cost types for measurement dropdown
  const { data: costTypes } = useData(TABLES.REFERENCE_COST_TYPES, {
    select: '*',
    orderBy: { column: 'name', ascending: true },
  });

  // Get existing draft if any
  const {
    data: existingDrafts,
    insert,
    update,
    remove,
  } = useData(TABLES.DRAFT_PROJECT_BOQ_ITEMS, {
    select: '*',
    match: {
      project_id: currentProject?.id,
      category_id: categoryId,
    },
  });

  // Unpack template items to flatten the data structure
  const unpackTemplateItems = React.useMemo(() => {
    if (!templateItems) return [];
    return templateItems.flatMap(template => {
      // Handle case where description might be a string
      const entries =
        typeof template.description === 'string'
          ? JSON.parse(template.description)
          : template.description;
      return (entries || []).map((entry, index) => ({
        id: `${template.id}_${index}`,
        template_id: template.id,
        entry_index: index,
        subject: entry.subject,
        text: entry.text,
        is_instruction: entry.is_instruction,
        default_selected: entry.default_selected || false,
        measurement_type_id: entry.measurement_type_id,
      }));
    });
  }, [templateItems]);

  // Group unpacked items by subject
  const itemsBySubject = React.useMemo(() => {
    return unpackTemplateItems.reduce((acc, item) => {
      if (!acc.has(item.subject)) {
        acc.set(item.subject, []);
      }
      acc.get(item.subject).push(item);
      return acc;
    }, new Map());
  }, [unpackTemplateItems]);

  // Clear all selections and switch to edit mode
  const handleClearAndEdit = async () => {
    if (
      !window.confirm('האם אתם בטוחים? פעולה זו תמחק את כל הבחירות הקיימות ותאפשר לכם להתחיל מחדש.')
    ) {
      return;
    }

    try {
      setIsLoading(true);
      // Remove existing draft if it exists
      if (existingDrafts?.[0]?.id) {
        await remove(existingDrafts[0].id);
      }
      // Reset local state
      setSelectedItems(new Map());
      setEditedText(new Map());
      setIsReadOnly(false);
    } catch (error) {
      logError('Failed to clear selections', {
        severity: ERROR_SEVERITY.ERROR,
        category: ERROR_CATEGORY.DATABASE,
        error,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Initialize selections and edited text from template defaults or existing draft
  useEffect(() => {
    const selections = new Map();
    const textEdits = new Map();

    // First set defaults from template
    unpackTemplateItems.forEach(item => {
      if (item.default_selected) {
        selections.set(item.id, {
          selected: true,
        });
      }
    });

    // Then override with any existing draft
    if (existingDrafts?.[0]?.selected_entries) {
      const draft = existingDrafts[0];
      setIsReadOnly(true); // Set read-only mode when opening existing draft
      Object.entries(draft.selected_entries).forEach(([id, entry]) => {
        selections.set(id, {
          selected: true,
          measurement_type_id: entry.measurement_type_id,
          measurement_type_name: entry.measurement_type_name,
        });
        if (entry.edited_text) {
          textEdits.set(id, entry.edited_text);
        }
      });
    }

    setSelectedItems(selections);
    setEditedText(textEdits);
  }, [existingDrafts, unpackTemplateItems]);

  // Set submit ref
  useEffect(() => {
    if (onSubmitRef) {
      onSubmitRef(() => handleSubmit);
    }
  }, [selectedItems]);

  const handleItemSelect = (templateId, entryIndex, costTypeId = null) => {
    const itemId = `${templateId}_${entryIndex}`;
    const newSelected = new Map(selectedItems);
    const currentItem = newSelected.get(itemId) || { selected: true };

    if (costTypeId) {
      const costType = costTypes?.find(ct => ct.id === costTypeId);
      newSelected.set(itemId, {
        ...currentItem,
        measurement_type_id: costTypeId,
        measurement_type_name: costType?.name,
      });
    } else {
      newSelected.set(itemId, currentItem);
    }
    setSelectedItems(newSelected);
  };

  const validateForm = () => {
    if (!selectedItems.size) {
      logError('No items selected', {
        severity: ERROR_SEVERITY.WARNING,
        category: ERROR_CATEGORY.VALIDATION,
        message: 'יש לבחור לפחות פריט אחד',
      });
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return false;

    try {
      setIsLoading(true);

      // Format selected items
      const selectedEntries = {};
      for (const [itemId, itemData] of selectedItems) {
        // Skip unselected items
        if (!itemData.selected) continue;

        const [templateId, entryIndex] = itemId.split('_');
        const template = templateItems?.find(t => t.id === templateId);
        const entry = template?.description?.[entryIndex];
        if (!template || !entry) {
          throw new Error(`Template item ${itemId} not found`);
        }

        selectedEntries[itemId] = {
          subject: entry.subject,
          text: editedText.get(itemId) || entry.text,
          is_instruction: entry.is_instruction,
          measurement_type_id: itemData.measurement_type_id,
          measurement_type_name: itemData.measurement_type_name,
        };
      }

      // Save draft - use update if exists, insert if new
      const existingDraft = existingDrafts?.[0];
      setIsReadOnly(true); // Switch to read-only after successful save
      if (existingDraft?.id) {
        await update(existingDraft.id, {
          selected_entries: selectedEntries,
        });
      } else {
        await insert({
          project_id: currentProject.id,
          category_id: categoryId,
          selected_entries: selectedEntries,
        });
      }

      try {
        if (onSubmit) {
          await onSubmit();
        }
        onClose();
        return true;
      } catch (error) {
        logError('Failed to execute onSubmit callback', {
          severity: ERROR_SEVERITY.ERROR,
          category: ERROR_CATEGORY.CALLBACK,
          error,
        });
        return false;
      }
    } catch (error) {
      logError('Failed to save template selections', {
        severity: ERROR_SEVERITY.ERROR,
        category: ERROR_CATEGORY.DATABASE,
        error,
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const isInitialLoading = !templateItems;
  if (!open) return null;

  if (isInitialLoading) {
    return (
      <div className={`${modalStyles.modalLarge} ${styles['wide-modal']}`} dir="rtl">
        <div className={modalStyles.header}>
          <h2 className={modalStyles.headerTitle}>טוען...</h2>
        </div>
      </div>
    );
  }

  return (
    <div className={`${modalStyles.modalLarge} ${styles['wide-modal']}`} dir="rtl">
      <div className={modalStyles.header}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <h2 className={modalStyles.headerTitle}>בחר פריטים מהתבנית</h2>
          {isReadOnly && (
            <button
              className={modalStyles.secondaryButton}
              onClick={handleClearAndEdit}
              disabled={isLoading}
            >
              נקה הכל והתחל מחדש
            </button>
          )}
        </div>
        <p className={modalStyles.headerSubtitle}>
          {isReadOnly ? 'הפריטים שנבחרו:' : 'בחרו את הפריטים ואת סוג המדידה עבורם'}
        </p>
        {!isReadOnly && <p className={modalStyles.headerHint}>ניתן לשנות את הבחירה בכל שלב</p>}
      </div>

      <div className={modalStyles.content}>
        {Array.from(itemsBySubject.entries()).map(([subject, items]) => (
          <div key={subject} className={modalStyles.field}>
            <div className={styles['item-type']}>{subject}</div>

            {items.map(item => (
              <div key={item.id} className={styles['form-item']}>
                <div className={styles['form-row']}>
                  {!isReadOnly && (
                    <input
                      type="checkbox"
                      checked={selectedItems.get(item.id)?.selected || false}
                      onChange={() => {
                        const newSelected = new Map(selectedItems);
                        const currentData = newSelected.get(item.id);
                        if (currentData?.selected) {
                          // If deselecting, remove the item
                          newSelected.delete(item.id);
                        } else {
                          // If selecting, add with default state
                          newSelected.set(item.id, {
                            selected: true,
                          });
                        }
                        setSelectedItems(newSelected);
                      }}
                      disabled={isLoading}
                    />
                  )}
                  <textarea
                    className={`${styles['form-text']} ${selectedItems.get(item.id)?.selected ? styles['selected'] : ''}`}
                    value={editedText.get(item.id) || item.text}
                    onChange={e => {
                      const newEdited = new Map(editedText);
                      newEdited.set(item.id, e.target.value);
                      setEditedText(newEdited);
                    }}
                    disabled={isLoading || isReadOnly}
                    readOnly={isReadOnly}
                  />
                </div>

                {!item.is_instruction &&
                  selectedItems.get(item.id)?.selected &&
                  (isReadOnly ? (
                    <div className={styles['measurement-type-text']}>
                      {selectedItems.get(item.id)?.measurement_type_name || 'לא נבחר סוג מדידה'}
                    </div>
                  ) : (
                    <select
                      className={styles['form-select']}
                      value={selectedItems.get(item.id)?.measurement_type_id || ''}
                      onChange={e =>
                        handleItemSelect(item.template_id, item.entry_index, e.target.value)
                      }
                      disabled={isLoading}
                    >
                      <option value="">בחרו סוג מידה</option>
                      {costTypes?.map(ct => (
                        <option key={ct.id} value={ct.id}>
                          {ct.name}
                        </option>
                      ))}
                    </select>
                  ))}
              </div>
            ))}
          </div>
        ))}
      </div>

      <div className={modalStyles.footer}>
        <button className={modalStyles.cancelButton} onClick={onClose} disabled={isLoading}>
          ביטול
        </button>
        <button
          className={modalStyles.primaryButton}
          onClick={handleSubmit}
          disabled={isLoading || !selectedItems.size}
        >
          {isLoading ? 'שומר...' : 'שמור'}
        </button>
      </div>
    </div>
  );
};

export default BOQTemplateModal;
